import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from './ui/dialog';
import { Badge } from './ui/badge';
import {
  ShieldCheck, Baby, AlertTriangle, Heart, Phone, Camera, PenTool, CheckCircle2, XCircle, Loader2, Download,
} from 'lucide-react';

const SOURCE_LABEL = {
  invitation_rsvp: 'Guest RSVP',
  public_web: 'Public link',
  qr: 'QR code',
  portal: 'Customer portal',
};

const YesNo = ({ ok, label, testid }) => (
  <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ${ok ? 'bg-[#06D6A0]/15 text-[#0F8B5C]' : 'bg-[#F1EFEA] text-[#9A968C]'}`} data-testid={testid}>
    {ok ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />} {label}
  </span>
);

const FormCard = ({ f, idx, pdfUrl }) => {
  const allergies = [...(f.allergies || []), f.allergy_other].filter(Boolean).join(', ') || 'None';
  const conditions = [...(f.medical_conditions || []), f.medical_other].filter(Boolean).join(', ') || 'None';
  return (
    <div className="rounded-2xl border border-[#E8E5DF] p-4 bg-white" data-testid={`guest-form-card-${idx}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Baby className="w-4 h-4 text-[#FF3B6B]" />
          <span className="font-semibold text-[#1E1E24]">{f.attendee_name || 'Guest'}</span>
        </div>
        <div className="flex items-center gap-2">
          {pdfUrl && f.form_id && (
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-full bg-[#FF3B6B] text-white hover:bg-[#E62E58] transition-colors"
              title="Download a signed PDF copy"
              data-testid={`gf-download-pdf-${idx}`}
            >
              <Download className="w-3 h-3" /> Download PDF
            </a>
          )}
          <Badge variant="outline" className="text-[10px]">{SOURCE_LABEL[f.source] || f.source}</Badge>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[13px] text-[#4B4B55]">
        <div className="flex items-start gap-1.5"><AlertTriangle className="w-3.5 h-3.5 mt-0.5 text-[#E5484D]" /><span><strong>Allergies:</strong> {allergies}</span></div>
        <div className="flex items-start gap-1.5"><Heart className="w-3.5 h-3.5 mt-0.5 text-[#FF3B6B]" /><span><strong>Medical:</strong> {conditions}</span></div>
        <div className="flex items-start gap-1.5"><Phone className="w-3.5 h-3.5 mt-0.5 text-[#6366F1]" /><span><strong>Emergency:</strong> {f.emergency_contact_name || '—'} {f.emergency_contact_phone ? `· ${f.emergency_contact_phone}` : ''} {f.emergency_contact_relationship ? `(${f.emergency_contact_relationship})` : ''}</span></div>
        <div className="flex items-start gap-1.5"><PenTool className="w-3.5 h-3.5 mt-0.5 text-[#1E1E24]" /><span><strong>Signed:</strong> {f.signature_name || '—'} {f.signature_date ? `· ${f.signature_date}` : ''}</span></div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        <YesNo ok={f.emergency_treatment_consent} label="Emergency treatment" testid={`gf-emergency-${idx}`} />
        <YesNo ok={f.photo_video_consent} label="Photo / video" testid={`gf-photo-${idx}`} />
        <YesNo ok={f.waiver_accepted} label="Liability waiver" testid={`gf-waiver-${idx}`} />
      </div>
    </div>
  );
};

/**
 * Modal listing every completed guest waiver/consent form for a booking or
 * invitation. Pass a `loader` async function that returns { forms: [...] }.
 */
export const GuestFormsDialog = ({ open, onOpenChange, loader, title = 'Completed forms', pdfUrlFor }) => {
  const [loading, setLoading] = useState(false);
  const [forms, setForms] = useState([]);
  const [err, setErr] = useState('');
  const loaderRef = useRef(loader);
  loaderRef.current = loader;

  useEffect(() => {
    if (!open || !loaderRef.current) return;
    let cancelled = false;
    setLoading(true); setErr('');
    loaderRef.current()
      .then((data) => { if (!cancelled) setForms(data?.forms || []); })
      .catch((e) => { if (!cancelled) setErr(e?.response?.data?.detail || 'Could not load forms'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-[#FFFDF8] max-h-[85vh] overflow-y-auto" data-testid="guest-forms-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-[#FF3B6B]" /> {title}</DialogTitle>
          <DialogDescription>Full details of every completed liability / consent form linked to this party.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-10 text-center text-[#6B6B7A]"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
        ) : err ? (
          <p className="py-8 text-center text-sm text-[#E5484D]">{err}</p>
        ) : forms.length === 0 ? (
          <p className="py-10 text-center text-sm text-[#6B6B7A]">No completed forms yet.</p>
        ) : (
          <div className="space-y-3">
            <p className="text-xs text-[#6B6B7A]">{forms.length} completed form{forms.length === 1 ? '' : 's'}</p>
            {forms.map((f, i) => <FormCard key={f.form_id || i} f={f} idx={i} pdfUrl={pdfUrlFor ? pdfUrlFor(f) : null} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default GuestFormsDialog;
